import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { dashboardApi } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useUIMode } from "@/hooks/useUIMode";
import { FreshnessIndicator } from "@/components/states/FreshnessIndicator";
import { CortexBranchesPulse } from "@/components/dashboard/CortexBranchesPulse";
import { StmBudgetWidget } from "@/components/dashboard/StmBudgetWidget";
import {
  Users, MessageSquare, UsersRound, TrendingUp, TrendingDown, BarChart3,
  Brain, DollarSign, Zap, ShieldAlert, Activity, Clock,
  Workflow, Eye, Sparkles, AlertTriangle, ShieldOff, ShieldCheck,
  Bell, LifeBuoy, ArrowRight, CheckCircle2, MessageCircle, Building2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell,
} from "recharts";
import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

/* ── Animation helper ── */
const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 16 } as const,
  animate: { opacity: 1, y: 0 } as const,
  transition: { duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
});

/* ── Chart styles ── */
const tooltipStyle = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 10,
  color: "hsl(var(--foreground))",
  boxShadow: "0 4px 16px -4px hsl(var(--foreground) / 0.1)",
  padding: "8px 12px",
  fontSize: 13,
};
const COLORS = [
  "hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
  "hsl(var(--chart-4))", "hsl(var(--chart-5))",
];

/* ── Number formatter ── */
function fmtNum(n: number | undefined | null, prefix = "") {
  if (n == null) return "-";
  if (n >= 1000) return `${prefix}${(n / 1000).toFixed(1)}k`;
  return `${prefix}${n}`;
}

function trendPct(curr: number, prev: number): { pct: number; up: boolean } | null {
  if (!prev && !curr) return null;
  if (!prev) return { pct: 100, up: true };
  const pct = Math.round(((curr - prev) / prev) * 100);
  return { pct: Math.abs(pct), up: pct >= 0 };
}

export default function Dashboard() {
  const { isAdvanced } = useUIMode();

  /* ── Core metrics ── */
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ["dashboard-metrics"],
    queryFn: dashboardApi.getMetrics,
    refetchInterval: 30_000,
  });
  const { data: messagesPerDay = [] } = useQuery({
    queryKey: ["dashboard-messages-per-day"],
    queryFn: dashboardApi.getMessagesPerDay,
    refetchInterval: 60_000,
  });
  const { data: messagesPerGroup = [] } = useQuery({
    queryKey: ["dashboard-messages-per-group"],
    queryFn: dashboardApi.getMessagesPerGroup,
  });

  /* ── Trends (semana atual vs anterior) ── */
  const { data: msgTrend } = useQuery({
    queryKey: ["dash-msg-trend"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const now = new Date();
      const d7 = new Date(now.getTime() - 7 * 86400000).toISOString();
      const d14 = new Date(now.getTime() - 14 * 86400000).toISOString();
      const [curr, prev] = await Promise.all([
        supabase.from("syra_messages").select("id", { count: "exact", head: true }).gte("created_at", d7),
        supabase.from("syra_messages").select("id", { count: "exact", head: true }).gte("created_at", d14).lt("created_at", d7),
      ]);
      return { curr: curr.count ?? 0, prev: prev.count ?? 0 };
    },
  });

  /* ── AI Costs ── */
  const { data: aiCosts, error: aiCostsError } = useQuery({
    queryKey: ["dash-ai-costs"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
      const { data, error } = await supabase
        .from("model_decisions")
        .select("estimated_cost_usd, model_used, success, was_fallback, created_at")
        .gte("created_at", thirtyDaysAgo);
      if (error) throw error;
      if (!data?.length)
        return { totalCost: 0, calls: 0, successRate: 0, fallbackRate: 0, byModel: [] };
      const totalCost = data.reduce((s, d) => s + (d.estimated_cost_usd || 0), 0);
      const successCount = data.filter((d) => d.success).length;
      const fallbackCount = data.filter((d) => d.was_fallback).length;
      const modelMap = new Map<string, number>();
      data.forEach((d) => modelMap.set(d.model_used, (modelMap.get(d.model_used) || 0) + 1));
      const byModel = Array.from(modelMap.entries())
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);
      return {
        totalCost,
        calls: data.length,
        successRate: successCount / data.length,
        fallbackRate: fallbackCount / data.length,
        byModel,
      };
    },
  });

  /* ── Skills ── */
  const { data: skillStats = [] } = useQuery({
    queryKey: ["dash-skill-stats"],
    queryFn: async () => {
      const { data, error } = await supabase.from("skill_feedback").select("skill_name, execution_time_ms");
      if (error) throw error;
      if (!data?.length) return [];
      const map = new Map<string, { count: number; totalMs: number }>();
      data.forEach((f) => {
        const cur = map.get(f.skill_name) || { count: 0, totalMs: 0 };
        cur.count++;
        cur.totalMs += f.execution_time_ms || 0;
        map.set(f.skill_name, cur);
      });
      return Array.from(map.entries())
        .map(([name, s]) => ({ name, count: s.count, avgMs: Math.round(s.totalMs / s.count) }))
        .sort((a, b) => b.count - a.count);
    },
  });

  /* ── Group risk ── */
  const { data: groupProfiles = [] } = useQuery({
    queryKey: ["dash-group-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("group_profiles")
        .select("churn_risk, participation_density, typology, syra_groups(name)")
        .order("churn_risk", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data || [];
    },
  });

  /* ── Última mensagem (freshness) ── */
  const { data: lastMessage } = useQuery({
    queryKey: ["dash-last-message"],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_messages")
        .select("created_at")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.created_at ? new Date(data.created_at) : null;
    },
  });

  /* ── Últimas 5 conversas ── */
  const { data: recentMessages = [] } = useQuery({
    queryKey: ["dash-recent-messages"],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_messages")
        .select("id, content, sender_name, created_at, group_id, syra_groups(name)")
        .order("created_at", { ascending: false })
        .limit(5);
      return data || [];
    },
  });

  /* ── Alertas e tickets abertos ── */
  const { data: openItems } = useQuery({
    queryKey: ["dash-open-items"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 86400000).toISOString();
      const [alerts, tickets, halluc] = await Promise.all([
        supabase.from("alert_dispatches").select("id, priority, channel, created_at, payload", { count: "exact" })
          .is("acknowledged_at", null).gte("created_at", since).order("created_at", { ascending: false }).limit(5),
        supabase.from("support_tickets").select("id, subject, priority, status, created_at, requester_name", { count: "exact" })
          .in("status", ["open", "pending"]).order("created_at", { ascending: false }).limit(5),
        supabase.from("hallucination_events").select("id", { count: "exact", head: true }).gte("created_at", since),
      ]);
      return {
        alerts: alerts.data || [],
        alertsCount: alerts.count ?? 0,
        tickets: tickets.data || [],
        ticketsCount: tickets.count ?? 0,
        hallucCount: halluc.count ?? 0,
      };
    },
  });

  /* ── Cortex KPIs (24h) ── */
  const { data: cortexKpis } = useQuery({
    queryKey: ["dash-cortex-kpis"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 86400000).toISOString();
      const [turns, drops, events] = await Promise.all([
        supabase.from("turn_trace").select("turn_id", { count: "exact", head: true }).gte("created_at", since),
        supabase.from("turn_trace").select("id", { count: "exact", head: true }).gte("created_at", since).eq("step", "inhibitor").eq("ok", false),
        supabase.from("cortex_events_aggregated_daily").select("total_count").gte("day", since.slice(0, 10)),
      ]);
      const eventsTotal = ((events.data as any[]) || []).reduce((s, r) => s + (r.total_count || 0), 0);
      return { turns: turns.count ?? 0, drops: drops.count ?? 0, events: eventsTotal };
    },
  });

  const highRiskGroups = groupProfiles.filter((p: any) => (p.churn_risk || 0) >= 0.7);
  const lastMsgAgeMs = lastMessage ? Date.now() - lastMessage.getTime() : null;
  const isDisconnected = lastMsgAgeMs !== null && lastMsgAgeMs > 60 * 60 * 1000;
  const isHealthy = !isDisconnected && lastMsgAgeMs !== null;

  const msgTrendData = msgTrend ? trendPct(msgTrend.curr, msgTrend.prev) : null;

  const totalActionItems =
    (openItems?.alertsCount ?? 0) + (openItems?.ticketsCount ?? 0) + highRiskGroups.length;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto pb-8">
      {/* ── Header ── */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-headline tracking-tight">
            Início
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {totalActionItems > 0
              ? `${totalActionItems} ${totalActionItems === 1 ? "item precisa" : "itens precisam"} da sua atenção`
              : "Tudo sob controle por aqui"}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button asChild variant="default" size="sm" className="gap-1.5">
            <Link to="/organizations">
              <Building2 className="w-3.5 h-3.5" />
              ORGs
            </Link>
          </Button>
          <Badge variant="outline" className="gap-1.5 text-[11px]">
            <Sparkles className="w-3 h-3" />
            Modo {isAdvanced ? "Avançado" : "Simples"}
          </Badge>
          <FreshnessIndicator updatedAt={lastMessage} prefix="Última mensagem há" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span>Atualiza a cada 30s</span>
          </div>
        </div>
      </div>

      {/* ── Status banner ── */}
      {isDisconnected ? (
        <Card className="border-destructive/40 bg-destructive/10">
          <CardContent className="p-3 text-sm flex items-center gap-3">
            <ShieldOff className="w-4 h-4 text-destructive shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-destructive">SYRA pode estar desconectada do WhatsApp</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Nenhuma mensagem há mais de 1 hora. Verifique o webhook da Evolution API.
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/health">Diagnóstico</Link>
            </Button>
          </CardContent>
        </Card>
      ) : isHealthy ? (
        <Card className="border-success/30 bg-success/5">
          <CardContent className="p-2.5 text-sm flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-success shrink-0" />
            <span className="text-foreground/90">SYRA conectada e recebendo mensagens em tempo real.</span>
          </CardContent>
        </Card>
      ) : null}

      {error && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="p-3 text-sm text-destructive flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-destructive shrink-0" />
            Erro ao carregar métricas: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {/* ── Linha 1: Cards de AÇÃO (alertas / tickets / alucinações) ── */}
      <motion.div {...fadeUp(0.05)} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ActionCard
          title="Alertas abertos"
          count={openItems?.alertsCount ?? 0}
          subtitle={openItems?.alertsCount ? "Não-acknowledged nos últimos 7d" : "Nenhum alerta pendente"}
          icon={Bell}
          tone={openItems?.alertsCount ? "destructive" : "neutral"}
          to="/sentinel"
          ctaLabel="Ver alertas"
        />
        <ActionCard
          title="Tickets de suporte"
          count={openItems?.ticketsCount ?? 0}
          subtitle={openItems?.ticketsCount ? "Aguardando resposta" : "Caixa zerada"}
          icon={LifeBuoy}
          tone={openItems?.ticketsCount ? "warning" : "neutral"}
          to="/support"
          ctaLabel="Abrir suporte"
        />
        <ActionCard
          title="Alucinações detectadas"
          count={openItems?.hallucCount ?? 0}
          subtitle={openItems?.hallucCount ? "Últimos 7 dias — revisar" : "Nenhuma nos últimos 7d"}
          icon={AlertTriangle}
          tone={openItems?.hallucCount && openItems.hallucCount > 5 ? "destructive" : openItems?.hallucCount ? "warning" : "success"}
          to="/quality-dashboard"
          ctaLabel="Ver qualidade"
        />
      </motion.div>

      {/* ── Linha 2: KPIs com tendência ── */}
      <motion.div {...fadeUp(0.1)} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          title="Mensagens hoje"
          value={isLoading ? null : metrics?.messages_today ?? 0}
          icon={MessageSquare}
          tone="primary"
          trend={msgTrendData}
          trendLabel="vs semana anterior"
        />
        <KpiCard
          title="Grupos ativos"
          value={isLoading ? null : metrics?.active_groups ?? 0}
          icon={UsersRound}
          tone="info"
          subtitle={`${metrics?.total_members ?? 0} membros no total`}
        />
        <KpiCard
          title="Custo IA (30d)"
          value={aiCosts ? `$${aiCosts.totalCost.toFixed(2)}` : null}
          icon={DollarSign}
          tone="warning"
          subtitle={aiCosts?.calls ? `${fmtNum(aiCosts.calls)} chamadas` : "Aguardando primeiras chamadas"}
          emptyAction={!aiCosts?.calls ? { to: "/ai-providers", label: "Configurar" } : undefined}
        />
        <KpiCard
          title="Taxa de sucesso IA"
          value={aiCosts?.calls ? `${Math.round(aiCosts.successRate * 100)}%` : null}
          icon={Activity}
          tone={aiCosts && aiCosts.successRate >= 0.9 ? "success" : aiCosts && aiCosts.successRate >= 0.7 ? "warning" : "destructive"}
          subtitle={aiCosts?.fallbackRate ? `${Math.round(aiCosts.fallbackRate * 100)}% via fallback` : "—"}
          emptyAction={!aiCosts?.calls ? { to: "/model-decisions", label: "Ver decisões" } : undefined}
        />
      </motion.div>

      {/* ── Linha 3: Gráfico principal + Cortex pulse ── */}
      <motion.div {...fadeUp(0.15)} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Mensagens 7d (col-span-2) */}
        <Card className="border-border/50 lg:col-span-2">
          <CardHeader className="pb-1 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Mensagens recebidas
              </CardTitle>
              <Badge variant="outline" className="text-[10px] font-normal">Últimos 7 dias</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {messagesPerDay.length === 0 ? (
              <EmptyChart
                icon={TrendingUp}
                title="Sem mensagens ainda"
                hint="Conecte um grupo do WhatsApp para começar"
                cta={{ to: "/groups", label: "Conectar grupo" }}
              />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={messagesPerDay}>
                  <defs>
                    <linearGradient id="msgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickFormatter={(v) => v.slice(5)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    axisLine={false} tickLine={false} width={32} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2}
                    fill="url(#msgGrad)" dot={{ fill: "hsl(var(--primary))", strokeWidth: 0, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--card))" }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Cortex Pulse */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Workflow className="w-4 h-4 text-primary" />
              Cortex OS · 24h
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <CortexStat label="Turnos processados" value={fmtNum(cortexKpis?.turns)} icon={Activity} />
            <CortexStat label="Drops do inibidor" value={fmtNum(cortexKpis?.drops)} icon={AlertTriangle} tone={cortexKpis?.drops ? "warning" : "neutral"} />
            <CortexStat label="Eventos Cortex" value={fmtNum(cortexKpis?.events)} icon={Brain} />
            <div className="pt-2 border-t border-border/40 flex flex-col gap-1.5">
              <Button asChild variant="outline" size="sm" className="w-full justify-between">
                <Link to="/cortex-os">Fluxo de decisão <ArrowRight className="w-3.5 h-3.5" /></Link>
              </Button>
              {isAdvanced && (
                <Button asChild variant="ghost" size="sm" className="w-full justify-between">
                  <Link to="/governanca/forense">Forense <Eye className="w-3.5 h-3.5" /></Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* ── Linha 3.5: Cortex Hardening branches (banter/reaffirmation/directive) em tempo real ── */}
      <CortexBranchesPulse />

      {/* ── Linha 3.6: STM budget — health + alerta de modelo desconhecido ── */}
      <StmBudgetWidget />

      {/* ── Linha 4: Acionável — conversas + grupos em risco + ações pendentes ── */}
      <motion.div {...fadeUp(0.2)} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Últimas conversas */}
        <Card className="border-border/50">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-primary" />
              Últimas conversas
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {recentMessages.length === 0 ? (
              <EmptyChart icon={MessageCircle} title="Sem mensagens recentes" />
            ) : (
              <div className="space-y-2.5">
                {recentMessages.map((m: any) => (
                  <Link
                    key={m.id}
                    to={`/conversations?group=${m.group_id}`}
                    className="block group hover:bg-accent/50 rounded-md p-2 -m-2 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2 text-xs mb-0.5">
                      <span className="font-medium text-foreground truncate">
                        {m.syra_groups?.name || "Grupo"}
                      </span>
                      <span className="text-muted-foreground tabular-nums shrink-0">
                        {formatDistanceToNow(new Date(m.created_at), { locale: ptBR, addSuffix: false })}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      <span className="text-foreground/70">{m.sender_name || "?"}:</span>{" "}
                      {m.content || "(sem texto)"}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Grupos em risco */}
        <Card className="border-border/50">
          <CardHeader className="pb-1 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ShieldAlert className={`w-4 h-4 ${highRiskGroups.length ? "text-destructive" : "text-success"}`} />
                Grupos em risco
              </CardTitle>
              {highRiskGroups.length > 0 && (
                <Badge variant="destructive" className="text-[10px]">{highRiskGroups.length}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {groupProfiles.length === 0 ? (
              <EmptyChart icon={ShieldAlert} title="Sem perfis de grupo ainda" hint="Os perfis aparecem após o primeiro ciclo de análise." />
            ) : highRiskGroups.length === 0 ? (
              <div className="flex items-center gap-2.5 py-3 text-sm text-success">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>Todos os grupos saudáveis</span>
              </div>
            ) : (
              <div className="space-y-2.5">
                {highRiskGroups.slice(0, 5).map((p: any, i: number) => {
                  const riskPct = Math.round((p.churn_risk || 0) * 100);
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="truncate text-foreground">{p.syra_groups?.name || "Grupo"}</span>
                        <span className="text-xs font-semibold tabular-nums text-destructive">{riskPct}%</span>
                      </div>
                      <Progress value={riskPct} className="h-1.5 [&>div]:bg-destructive" />
                    </div>
                  );
                })}
                <Button asChild variant="ghost" size="sm" className="w-full mt-2 justify-between text-xs">
                  <Link to="/groups">Ver todos os grupos <ArrowRight className="w-3 h-3" /></Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tickets / alertas em destaque */}
        <Card className="border-border/50">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Bell className="w-4 h-4 text-warning" />
              Itens pendentes
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-3">
            {(openItems?.alerts.length ?? 0) === 0 && (openItems?.tickets.length ?? 0) === 0 ? (
              <div className="flex items-center gap-2.5 py-3 text-sm text-success">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>Nada pendente — bom trabalho!</span>
              </div>
            ) : (
              <>
                {openItems?.alerts.slice(0, 3).map((a: any) => (
                  <Link key={a.id} to="/sentinel" className="block group hover:bg-accent/50 rounded-md p-2 -m-2 transition-colors">
                    <div className="flex items-start gap-2">
                      <Bell className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${a.priority === "high" || a.priority === "critical" ? "text-destructive" : "text-warning"}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground truncate">
                          {a.payload?.title || a.payload?.message || `Alerta ${a.priority}`}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {a.channel} · {formatDistanceToNow(new Date(a.created_at), { locale: ptBR, addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
                {openItems?.tickets.slice(0, 3).map((t: any) => (
                  <Link key={t.id} to="/support" className="block group hover:bg-accent/50 rounded-md p-2 -m-2 transition-colors">
                    <div className="flex items-start gap-2">
                      <LifeBuoy className="w-3.5 h-3.5 mt-0.5 shrink-0 text-info" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground truncate">{t.subject}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {t.requester_name || "—"} · {formatDistanceToNow(new Date(t.created_at), { locale: ptBR, addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* ── Linha 5: Avançado — modelos + grupos por volume + skills ── */}
      {isAdvanced && (
        <motion.div {...fadeUp(0.25)} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Distribuição de modelos */}
          <Card className="border-border/50">
            <CardHeader className="pb-1 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Brain className="w-4 h-4 text-primary" />
                Distribuição de modelos
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-3">
              {!aiCosts?.byModel?.length ? (
                <EmptyChart icon={Brain} title="Sem decisões ainda" cta={{ to: "/model-policies", label: "Configurar políticas" }} />
              ) : (
                <div className="flex items-center gap-2">
                  <ResponsiveContainer width="55%" height={180}>
                    <PieChart>
                      <Pie data={aiCosts.byModel} cx="50%" cy="50%" innerRadius={36} outerRadius={70}
                        dataKey="value" paddingAngle={3} strokeWidth={0}>
                        {aiCosts.byModel.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5">
                    {aiCosts.byModel.map((m, i) => (
                      <div key={m.name} className="flex items-center gap-2 text-xs">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="truncate text-muted-foreground">{m.name.split("/").pop()}</span>
                        <span className="ml-auto font-medium text-foreground">{m.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mensagens por grupo */}
          <Card className="border-border/50">
            <CardHeader className="pb-1 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Top 5 grupos
                </CardTitle>
                <Badge variant="outline" className="text-[10px] font-normal">Volume</Badge>
              </div>
            </CardHeader>
            <CardContent className="px-2 pb-3">
              {messagesPerGroup.length === 0 ? (
                <EmptyChart icon={BarChart3} title="Sem dados" />
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={messagesPerGroup} barCategoryGap="25%">
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.85} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                    <XAxis dataKey="group_name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Skills */}
          <Card className="border-border/50">
            <CardHeader className="pb-1 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Zap className="w-4 h-4 text-warning" />
                Performance de skills
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              {skillStats.length === 0 ? (
                <EmptyChart icon={Zap} title="Sem execuções de skills ainda" cta={{ to: "/skills", label: "Configurar skills" }} />
              ) : (
                <div className="space-y-2.5 mt-1">
                  {skillStats.slice(0, 5).map((s) => (
                    <div key={s.name} className="flex items-center gap-3">
                      <span className="text-sm truncate flex-1 text-foreground">{s.name}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">{s.count}×</Badge>
                      <span className="text-xs text-muted-foreground w-14 text-right tabular-nums">
                        {s.avgMs >= 1000 ? `${(s.avgMs / 1000).toFixed(1)}s` : `${s.avgMs}ms`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}

/* ── Sub-components ────────────────────────────────────────────── */

type Tone = "primary" | "info" | "success" | "warning" | "destructive" | "neutral";

const toneClasses: Record<Tone, { bg: string; fg: string; border: string }> = {
  primary:     { bg: "bg-primary/10",     fg: "text-primary",     border: "border-primary/30" },
  info:        { bg: "bg-info/10",        fg: "text-info",        border: "border-info/30" },
  success:     { bg: "bg-success/10",     fg: "text-success",     border: "border-success/30" },
  warning:     { bg: "bg-warning/10",     fg: "text-warning",     border: "border-warning/30" },
  destructive: { bg: "bg-destructive/10", fg: "text-destructive", border: "border-destructive/30" },
  neutral:     { bg: "bg-muted/40",       fg: "text-muted-foreground", border: "border-border/60" },
};

function ActionCard({
  title, count, subtitle, icon: Icon, tone, to, ctaLabel,
}: {
  title: string; count: number; subtitle: string;
  icon: React.ElementType; tone: Tone; to: string; ctaLabel: string;
}) {
  const t = toneClasses[tone];
  const isZero = count === 0;
  return (
    <Card className={`${isZero ? "border-border/60" : t.border} bg-card`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl ${t.bg} ${t.fg} flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-bold font-headline tabular-nums ${isZero ? "text-muted-foreground" : t.fg}`}>{count}</span>
            <span className="text-xs text-muted-foreground truncate">{title}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{subtitle}</p>
        </div>
        <Button asChild variant="ghost" size="sm" className="shrink-0">
          <Link to={to}>{ctaLabel}<ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function KpiCard({
  title, value, icon: Icon, tone, subtitle, trend, trendLabel, emptyAction,
}: {
  title: string;
  value: number | string | null;
  icon: React.ElementType;
  tone: Tone;
  subtitle?: string;
  trend?: { pct: number; up: boolean } | null;
  trendLabel?: string;
  emptyAction?: { to: string; label: string };
}) {
  const t = toneClasses[tone];
  const isEmpty = value === null || value === 0 || value === "$0.00";
  return (
    <Card className="border-border/60 bg-card hover:border-border transition-colors">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</span>
          <div className={`w-8 h-8 rounded-lg ${t.bg} ${t.fg} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        {value === null ? (
          <Skeleton className="h-9 w-20" />
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-foreground font-headline tracking-tight">
              {typeof value === "number" ? fmtNum(value) : value}
            </span>
            {trend && (
              <span className={`text-xs font-medium flex items-center gap-0.5 ${trend.up ? "text-success" : "text-destructive"}`}>
                {trend.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {trend.pct}%
              </span>
            )}
          </div>
        )}
        {subtitle && !isEmpty && (
          <p className="text-[11px] text-muted-foreground mt-1.5 truncate">
            {trend && trendLabel ? trendLabel : subtitle}
          </p>
        )}
        {isEmpty && emptyAction && (
          <Button asChild variant="link" size="sm" className="px-0 h-auto text-xs mt-1.5">
            <Link to={emptyAction.to}>{emptyAction.label} <ArrowRight className="w-3 h-3 ml-1" /></Link>
          </Button>
        )}
        {!isEmpty && subtitle && trend && (
          <p className="text-[11px] text-muted-foreground/80 mt-0.5 truncate">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

function CortexStat({ label, value, icon: Icon, tone = "neutral" }: {
  label: string; value: string; icon: React.ElementType; tone?: Tone;
}) {
  const t = toneClasses[tone === "neutral" ? "primary" : tone];
  return (
    <div className="flex items-center gap-2.5">
      <Icon className={`w-3.5 h-3.5 shrink-0 ${tone === "warning" ? "text-warning" : "text-primary"}`} />
      <span className="text-xs text-muted-foreground flex-1">{label}</span>
      <span className={`text-sm font-bold font-headline tabular-nums ${tone === "warning" ? "text-warning" : "text-foreground"}`}>{value}</span>
    </div>
  );
}

function EmptyChart({
  icon: Icon, title = "Sem dados disponíveis", hint, cta,
}: {
  icon: React.ElementType;
  title?: string;
  hint?: string;
  cta?: { to: string; label: string };
}) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-muted-foreground text-center px-4">
      <Icon className="w-7 h-7 mb-2 opacity-25" />
      <p className="text-xs font-medium">{title}</p>
      {hint && <p className="text-[10px] mt-1 opacity-80">{hint}</p>}
      {cta && (
        <Button asChild variant="outline" size="sm" className="mt-3 h-7 text-xs">
          <Link to={cta.to}>{cta.label} <ArrowRight className="w-3 h-3 ml-1" /></Link>
        </Button>
      )}
    </div>
  );
}
